import React, { useCallback, useEffect, useMemo, useState } from "react";

import classNames from "classnames";
import { differenceBy } from "lodash/array";
import { cloneDeep } from "lodash/lang";
import PropTypes from "prop-types";
import { OverlayTrigger } from "react-bootstrap";
import { useTable } from "react-table";
import { ColumnsPopover, TableCheckbox, DndPaginator } from "./components";
import { Arrow } from "../Icon";
import "./styles.scss";
import DraggableTable from "./components/DraggableTable/DraggableTable";
import { SHOWING } from "../../constants/messages";

const DndTable = props => {
  const {
    columns,
    data,
    classes,
    tableHooks,
    isVisiblePopover,
    initialState,
    unhiddenColumns,
    onChangeColumnsSelection,
    defaultColumn,
    selectable,
    onOpenColumnPopover,
    manualSortBy,
    hideSelectionInPopover,
    autoSelectAllRows,
    useHookPagination,
    children,
  } = props;
  const checkboxColumn = useMemo(
    () => ({
      Header: ({
        getToggleAllRowsSelectedProps,
        getToggleAllPageRowsSelectedProps,
        data,
      }) => {
        const headerProps = useHookPagination
          ? getToggleAllPageRowsSelectedProps() // select only rows for the active page
          : getToggleAllRowsSelectedProps();

        return data?.length > 0 ? <TableCheckbox {...headerProps} /> : null;
      },
      accessor: "selection",
      width: 35,
      minWidth: 35,
      maxWidth: 35,
      Cell: ({ row }) => <TableCheckbox {...row.getToggleRowSelectedProps()} />,
    }),
    [useHookPagination]
  );

  const [isColumnsPopoverOpen, setIsColumnsPopoverOpen] = useState(false);
  const [isPopoverHidden, setIsPopoverHidden] = useState(false);
  const updatedColumns = useMemo(() => {
    const copyColumns = cloneDeep(columns);
    selectable && copyColumns.unshift(checkboxColumn);
    return selectable ? copyColumns : columns;
  }, [columns, selectable]);

  const {
    allColumns,
    visibleColumns,
    pageOptions,
    pageCount,
    gotoPage,
    nextPage,
    previousPage,
    state,
    ...tableProps
  } = useTable(
    {
      columns: updatedColumns,
      data,
      defaultColumn,
      initialState,
      autoResetSortBy: false,
      manualSortBy,
    },
    ...tableHooks
  );

  const onFirst = useCallback(() => gotoPage(0), [gotoPage]);
  const onLast = useCallback(() => gotoPage(pageCount - 1), [
    gotoPage,
    pageCount,
  ]);
  useEffect(() => {
    autoSelectAllRows && data.length && tableProps.toggleAllRowsSelected(true);
  }, [tableProps.toggleAllRowsSelected, data]);

  const { popoverColumnList, popoverVisibleColumns } = useMemo(() => {
    const popoverColumnList = differenceBy(
      selectable && hideSelectionInPopover
        ? allColumns.slice(1, allColumns.length)
        : allColumns,
      unhiddenColumns.map(value => ({ id: value })),
      "id"
    );
    const popoverVisibleColumns =
      selectable && hideSelectionInPopover
        ? visibleColumns.slice(1, visibleColumns.length)
        : visibleColumns;

    return { popoverColumnList, popoverVisibleColumns };
  }, [allColumns, visibleColumns]);

  return (
    <div className={classNames("mydpd-dndtable-wrapper", classes.wrapper)}>
      {isPopoverHidden && (
        // When an element is dragging, render a table wrapper to override all
        // active elements on the page and prevent stopping onDrop emitting
        <div
          style={{
            zIndex: 1,
            position: "fixed",
            top: 0,
            left: 0,
            width: "100vw",
            height: "100vh",
          }}
        />
      )}
      {isVisiblePopover && (
        <div
          className={classNames(
            "mydpd-dndtable-wrapper-popover",
            classes.trigger,
            isPopoverHidden && "mydpd-dndtable-wrapper-popover--hidden"
          )}
        >
          <OverlayTrigger
            onEnter={() => {
              setIsColumnsPopoverOpen(true);
              onOpenColumnPopover();
            }}
            onExit={() => setIsColumnsPopoverOpen(false)}
            rootClose={true}
            trigger="click"
            placement="bottom-end"
            overlay={overlayProps =>
              ColumnsPopover({
                ...overlayProps,
                columnList: popoverColumnList,
                onChangeColumnsSelection,
                visibleColumns: popoverVisibleColumns,
                classes: { popover: classes.popover, list: classes.list },
              })
            }
          >
            <span onClick={e => e.stopPropagation()}>
              <Arrow
                className={classNames(
                  "mydpd-dndtable-wrapper-popover-arrow",
                  isColumnsPopoverOpen &&
                    "mydpd-dndtable-wrapper-popover-arrow-rotated"
                )}
              />
            </span>
          </OverlayTrigger>
        </div>
      )}
      <DraggableTable
        tableProps={{ ...tableProps, visibleColumns, state }}
        {...props}
        selectable={selectable}
        setIsPopoverHidden={setIsPopoverHidden}
      >
        {children}
        {useHookPagination && (
          <DndPaginator
            page={state.pageIndex + 1}
            totalCount={data.length}
            pageSize={state.pageSize}
            statusText={SHOWING}
            onPrevious={previousPage}
            onNext={nextPage}
            onFirst={onFirst}
            onLast={onLast}
          />
        )}
      </DraggableTable>
    </div>
  );
};

DndTable.defaultProps = {
  classes: {},
  columns: [],
  data: [],
  tableHooks: [],
  initialState: {},
  unhiddenColumns: [],
  onChangeColumnsSelection: () => null,
  onOpenColumnPopover: () => null,
  isVisiblePopover: false,
  selectOnlyOneRow: false,
  unselectAll: false,
  manualSortBy: false,
  stretchColumnWidth: true,
  hideSelectionInPopover: false,
  autoSelectAllRows: false,
  useHookPagination: false,
};

DndTable.propTypes = {
  columns: PropTypes.array,
  data: PropTypes.array,
  classes: PropTypes.object,
  getToggleAllRowsSelectedProps: PropTypes.func,
  getToggleAllPageRowsSelectedProps: PropTypes.func,
  row: PropTypes.object,
  tableHooks: PropTypes.array,
  unhiddenColumns: PropTypes.array,
  onChangeColumnsSelection: PropTypes.func,
  onOpenColumnPopover: PropTypes.func,
  children: PropTypes.node,
  initialState: PropTypes.object,
  defaultColumn: PropTypes.object,
  isVisiblePopover: PropTypes.bool,
  selectable: PropTypes.bool,
  selectOnlyOneRow: PropTypes.bool,
  unselectAll: PropTypes.bool,
  autoSelectAllRows: PropTypes.bool,
  useHookPagination: PropTypes.bool,
  manualSortBy: PropTypes.bool,
  stretchColumnWidth: PropTypes.bool,
  tableRef: PropTypes.object,
  hideSelectionInPopover: PropTypes.bool,
};

export default DndTable;
